import * as PIXI from 'pixi.js';
import gsap from 'gsap';

import AssetsLoader from '../utils/assetsLoader';
import { getGlobalEventEmitter } from '../utils/globalEventEmitter';
import { CARD_HEIGHT, CARD_WIDTH, getProxiedCardImageUrl, OUTLINE_WIDTH, POWER_TEXT_SIZE, RESOLUTION, TEXT_SIZE, convertToVec3 } from '../utils/commonUtils';
import CardHighlightFilter from '../filters/CardHighlightFilter';

const COLOR_HASHES = {
    Red: '#BA2A2A',
    Green: '#23916b',
    Blue: '#0084bd',
    Purple: '#963788',
    Yellow: '#f6e649',
    Black: '#000000',
}

export class CardVisualsContainer extends PIXI.Container {
    static timer = 0;
    static highlightFilterSpeed = 0.0004;
    static summoningSicknessOffsetAngle = 0;

    constructor(cardInstance) {
        
        if (!CardVisualsContainer.setupStatics) {
            CardVisualsContainer.setupStatics = true;

            PIXI.Ticker.shared.add((ticker) => {
                CardVisualsContainer.summoningSicknessOffsetAngle += 2 * Math.PI * (ticker.deltaMS / 1000);
                CardVisualsContainer.timer = ticker.lastTime * CardVisualsContainer.highlightFilterSpeed;
            });
        }

        super();

        this.cardHighlightFilter = new CardHighlightFilter();
        this.cardHighlightFilter.resources.timeUniforms.uniforms.uTime = CardVisualsContainer.timer;
        
        const cardFadeTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.CARD_FADE);
        const cardHighlight = new PIXI.Sprite(cardFadeTexture);
        cardHighlight.visible = false;
        cardHighlight.eventMode = 'none';
        cardHighlight.label = 'cardHighlight';
        cardHighlight.anchor.set(0.5);
        cardHighlight.x = 0;
        cardHighlight.y = 0;
        cardHighlight.scale.set(CardHighlightFilter.initialValues.borderSize, CardHighlightFilter.initialValues.borderSize*(65/62));
        cardHighlight.filters = [this.cardHighlightFilter];
        this.addChild(cardHighlight);

        const blockerFrameHighlightMaskTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.BLOCKER_FRAME_HIGHLIGHT_MASK);
        const blockerFrameHighlightMask = new PIXI.Sprite(blockerFrameHighlightMaskTexture);
        blockerFrameHighlightMask.label = 'blockerFrameHighlightMask';
        blockerFrameHighlightMask.visible = false;
        blockerFrameHighlightMask.anchor.set(0.5);
        blockerFrameHighlightMask.x = 0;
        blockerFrameHighlightMask.y = 0;
        blockerFrameHighlightMask.scale.set(1.25);
        blockerFrameHighlightMask.filters = [this.cardHighlightFilter];
        this.addChild(blockerFrameHighlightMask);

        this.cardHighlightTicker = () => {
            this.cardHighlightFilter.resources.timeUniforms.uniforms.uTime = CardVisualsContainer.timer;
        }
        PIXI.Ticker.shared.add(this.cardHighlightTicker);

        const blockerFrameTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.BLOCKER_FRAME);
        const blockerFrame = new PIXI.Sprite(blockerFrameTexture);
        blockerFrame.label = 'blockerFrame';
        blockerFrame.visible = false;
        blockerFrame.anchor.set(0.5);
        blockerFrame.x = 0;
        blockerFrame.y = 0;
        this.addChild(blockerFrame);

        const effectActivationMask = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.FIRE_RING);
        const effectActivationSprite = new PIXI.Sprite(effectActivationMask);
        effectActivationSprite.visible = false;
        effectActivationSprite.label = 'effectActivationSprite';
        effectActivationSprite.anchor.set(0.5);
        effectActivationSprite.x = 0;
        effectActivationSprite.y = 0;
        effectActivationSprite.scale.set(0.7, 0.72);
        effectActivationSprite.filters = [this.cardHighlightFilter];
        this.addChild(effectActivationSprite);

        const frontFaceSprite = new PIXI.Sprite();
        frontFaceSprite.label = 'frontFaceSprite';
        if (cardInstance?.type === 'DON!!') {
            frontFaceSprite.texture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.DON_CARD_FACE);
        }
        frontFaceSprite.width = CARD_WIDTH;
        frontFaceSprite.height = CARD_HEIGHT;
        frontFaceSprite.anchor.set(0.5);
        this.addChild(frontFaceSprite);

        const backFaceSprite = new PIXI.Sprite();
        backFaceSprite.label = 'backFaceSprite';
        backFaceSprite.texture = PIXI.Assets.get(
            cardInstance?.type === 'DON!!'
                ? AssetsLoader.eDefaultAssetsAliases.DON_DECK_CARD_BACK 
                : AssetsLoader.eDefaultAssetsAliases.MAIN_DECK_CARD_BACK
        );
        backFaceSprite.width = CARD_WIDTH;
        backFaceSprite.height = CARD_HEIGHT;
        backFaceSprite.anchor.set(0.5);
        this.addChild(backFaceSprite);

        const cardGlowTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.CARD_GLOW);
        const cardGlow = new PIXI.Sprite(cardGlowTexture);
        cardGlow.visible = false;
        cardGlow.label = 'cardGlow';
        cardGlow.anchor.set(0.5);
        cardGlow.x = 0;
        cardGlow.y = 0;
        cardGlow.scale.set(0.88, 0.9);
        cardGlow.tint = '#3884ff';
        this.addChild(cardGlow);

        const restedOverlayTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.RESTED_OVERLAY);
        const restedOverlay = new PIXI.Sprite(restedOverlayTexture);
        restedOverlay.label = 'restedOverlay';
        restedOverlay.visible = false;
        restedOverlay.width = CARD_WIDTH + 2*RESOLUTION;
        restedOverlay.height = CARD_HEIGHT + 2*RESOLUTION;
        restedOverlay.anchor.set(0.5);
        this.addChild(restedOverlay);

        const restedZzzTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.RESTED_ZZZ);
        const restedZzz = new PIXI.Sprite(restedZzzTexture);
        restedZzz.label = 'restedZzz';
        restedZzz.visible = false;
        restedZzz.anchor.set(0.5);
        restedZzz.x = 0;
        restedZzz.y = 0;
        this.addChild(restedZzz);

        const summoningSicknessTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.SUMMONING_SICKNESS);
        const summoningSickness = new PIXI.Sprite(summoningSicknessTexture);
        summoningSickness.label = 'summoningSickness';
        summoningSickness.visible = false;
        summoningSickness.anchor.set(0.5);
        summoningSickness.x = Math.cos(CardVisualsContainer.summoningSicknessOffsetAngle) * 10;
        summoningSickness.y = Math.sin(CardVisualsContainer.summoningSicknessOffsetAngle) * 10;
        this.addChild(summoningSickness);

        this.summoningSicknessTicker = () => {
            summoningSickness.x = Math.cos(CardVisualsContainer.summoningSicknessOffsetAngle) * 10;
            summoningSickness.y = Math.sin(CardVisualsContainer.summoningSicknessOffsetAngle) * 10;
        }
        PIXI.Ticker.shared.add(this.summoningSicknessTicker);

        const circle01Texture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.CIRCLE_01);
        const circle01 = new PIXI.Sprite(circle01Texture);
        circle01.label = 'circle01';
        circle01.visible = false;
        circle01.anchor.set(0.5);
        circle01.x = 0;
        circle01.y = 0;
        circle01.scale.set(0.5);
        circle01.tint = '#fbe64e';
        this.addChild(circle01);

        const blockerIconTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.BLOCKER_ICON);
        const blockerIcon = new PIXI.Sprite(blockerIconTexture);
        blockerIcon.label = 'blockerIcon';
        blockerIcon.visible = false;
        blockerIcon.anchor.set(0.5);
        blockerIcon.x = 0;
        blockerIcon.y = 140;
        this.addChild(blockerIcon);

        const skullTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.SKULL);
        const skull = new PIXI.Sprite(skullTexture);
        skull.label = 'skull';
        skull.visible = false;
        skull.anchor.set(0.5);
        skull.x = 0;
        skull.y = 0;
        this.addChild(skull);

        const heartDamageSpritesheet = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.HEART_DAMAGE_SPRITESHEET);
        const heartDamageAnimatedSprite = new PIXI.AnimatedSprite(Object.values(heartDamageSpritesheet.textures));
        heartDamageAnimatedSprite.label = 'heartDamageAnimatedSprite';
        heartDamageAnimatedSprite.visible = false;
        heartDamageAnimatedSprite.anchor.set(0.5);
        heartDamageAnimatedSprite.x = 0;
        heartDamageAnimatedSprite.y = 0;
        heartDamageAnimatedSprite.animationSpeed = 0.07;
        heartDamageAnimatedSprite.loop = false;
        this.addChild(heartDamageAnimatedSprite);

        const glowingCardEffectTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.WHITE_CARD);
        const glowingCardEffect = new PIXI.Sprite(glowingCardEffectTexture);
        glowingCardEffect.label = 'glowingCardEffect';
        glowingCardEffect.visible = false;
        glowingCardEffect.anchor.set(0.5);
        glowingCardEffect.x = 0;
        glowingCardEffect.y = 0;
        glowingCardEffect.width = CARD_WIDTH;
        glowingCardEffect.height = CARD_HEIGHT;
        this.addChild(glowingCardEffect);
    }

    setHighlightParameters(borderStrength, effectStrength, effectColor) {
        this.cardHighlightFilter.resources.effectUniforms.uniforms.uBorderStrength = borderStrength;
        this.cardHighlightFilter.resources.effectUniforms.uniforms.uEffectStrength = effectStrength;
        this.cardHighlightFilter.resources.effectUniforms.uniforms.uEffectColor = convertToVec3(effectColor);
    }

    destroy() {
        PIXI.Ticker.shared.remove(this.cardHighlightTicker);
        PIXI.Ticker.shared.remove(this.summoningSicknessTicker);
        super.destroy();
    }
}

export default class CardContainer extends PIXI.Container {
    #showRested = true;

    constructor(cardInstance, options = {}) {
        super();

        const { powerTextYOffset = 28 } = options;

        const cardVisualsContainer = new CardVisualsContainer(cardInstance);
        cardVisualsContainer.label = 'cardVisualsContainer';
        cardVisualsContainer.y = -CARD_HEIGHT / 2;
        this.addChild(cardVisualsContainer);

        this.eventMode = 'static';
        let timeout;
        let cardHeldInitialPosition;
        let pointerDownOnThisCard = false;
        this.isEventsDisabled = false;
        this.isTappedDisabled = false;
        this.shouldStopEventPropagation = true;


        this.on('pointerdown', (event) => {
            if (this.isEventsDisabled) return;

            pointerDownOnThisCard = true;

            if (this.shouldStopEventPropagation) {
                event.stopPropagation();
            }

            console.log("Card clicked", event);
            cardHeldInitialPosition = event.global.clone();
            console.log("Card held initial position", cardHeldInitialPosition);

            timeout = setTimeout(() => {
                pointerDownOnThisCard = false;
                const eventEmitter = getGlobalEventEmitter();
                eventEmitter?.emit('cardHeld', this);
                this.onCardHeld?.();
            }, 700);
        });

        this.on('pointerup', (event) => {
            if (this.isEventsDisabled) return;

            if (this.shouldStopEventPropagation) {
                event.stopPropagation();
            }

            clearTimeout(timeout);

            if (pointerDownOnThisCard && !this.isTappedDisabled) {
                const eventEmitter = getGlobalEventEmitter();
                eventEmitter?.emit('cardTapped', this);
                console.log("Card Tapped");
            }

            pointerDownOnThisCard = false;
        });

        this.on('globalpointermove', (event) => {
            if (this.isEventsDisabled) return;

            if (!cardHeldInitialPosition) return;

            const distance = cardHeldInitialPosition.subtract(event.global).magnitude();
            if (distance > 70) {
                pointerDownOnThisCard = false;
                clearTimeout(timeout);
                cardHeldInitialPosition = null;
            }
        });

        const powerTextContainer = new PIXI.Container();
        powerTextContainer.label = 'powerTextContainer';
        powerTextContainer.x = 0;
        powerTextContainer.y = powerTextYOffset;

        const powerText = new PIXI.Text({
            text: '',
            style: {
                fontFamily: 'Fugaz One',
                fontWeight: '400',
                fontSize: POWER_TEXT_SIZE,
                fill: '#ffffff',
                stroke: { color: '#000000', width: 10 },
            }
        });
        powerText.label = 'powerText';
        powerText.anchor.set(0.5, 1);
        powerTextContainer.addChild(powerText);
        this.addChild(powerTextContainer);

        this.costContainer = new PIXI.Container();
        this.costContainer.label = 'costContainer';
        this.costContainer.visible = true;
        this.costContainer.x = -CARD_WIDTH / 2 + 15;
        this.costContainer.y = -CARD_HEIGHT + 25;
        this.addChild(this.costContainer);

        this.rebuildCostContainer();

        const donCountContainer = new PIXI.Container();
        donCountContainer.label = 'donCountContainer';
        donCountContainer.visible = false;
        donCountContainer.x = 0;
        donCountContainer.y = -CARD_HEIGHT - 20;
        this.addChild(donCountContainer);

        const donCountTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.CARD_DON_COUNT);
        const donCountSprite = new PIXI.Sprite(donCountTexture);
        donCountSprite.anchor.set(1, 1);
        donCountSprite.x = 5*RESOLUTION;
        donCountSprite.y = 11*RESOLUTION;
        donCountSprite.scale.set(0.42);
        donCountContainer.addChild(donCountSprite);

        const donCountText = new PIXI.Text({
            text: '',
            style: {
                fontFamily: 'Fugaz One',
                fontSize: 60,
                fill: '#d11c27',
                stroke: { color: '#ffffff', width: 6 },
                dropShadow: {
                    distance: 4,
                    color: '#000000',
                    angle: 1.5,
                },
            }
        });
        donCountText.label = 'donCountText';
        donCountText.anchor.set(0, 1);
        donCountText.x = 5*RESOLUTION;
        donCountText.y = 11*RESOLUTION;
        donCountContainer.addChild(donCountText);

        this.updateCardInstance(cardInstance);

        this.name = cardInstance?.instanceId;
    }

    rebuildCostContainer(color = 'Red', cost = 10) {
        const COST_BG_RADIUS = 35;

        let colorHash = COLOR_HASHES[color] || '#C92828';

        this.costContainer.removeChildren();

        const bgGraphics = new PIXI.Graphics();
        bgGraphics.visible = true;
        bgGraphics.x = 0;
        bgGraphics.y = 0;
        bgGraphics.circle(0, 0, COST_BG_RADIUS);
        bgGraphics.fill({ color: colorHash });
        bgGraphics.stroke({ color: '#ffffff', width: 6 });
        this.costContainer.addChild(bgGraphics);
        
        const costText = new PIXI.Text({
            text: cost.toString(),
            style: {
                fontFamily: 'Fugaz One',
                fontSize: 40,
                fill: '#ffffff',
            }
        });
        costText.anchor.set(0.5);
        costText.x = -1;
        costText.y = -1;
        this.costContainer.addChild(costText);
    }

    updateCardInstance(cardInstance) {
        this.cardInstance = cardInstance;

        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');

        if (this?.cardInstance?.imgUrl) {
            cardVisualsContainer.getChildByLabel('frontFaceSprite').texture = PIXI.Assets.get(getProxiedCardImageUrl(this.cardInstance.imgUrl));
            this.showFrontFace();
        } else {
            this.showBackFace();
        }

        this.#updateRestedVisuals();

        if (this?.cardInstance?.currentPower || this?.cardInstance?.currentPower === 0) {
            const powerTextContainer = this.getChildByLabel('powerTextContainer');
            const powerText = powerTextContainer.children[0];
            powerTextContainer.visible = true;

            powerText.style.fill = this.#calculatePowerTextColor(this.cardInstance.power, this.cardInstance.currentPower);

            powerText.text = this.cardInstance.currentPower.toString();
        } else {
            this.getChildByLabel('powerTextContainer').visible = false;
        }

        if (this?.cardInstance?.attachedDon?.length) {
            const donCountContainer = this.getChildByLabel('donCountContainer');
            const donCountText = donCountContainer.getChildByLabel('donCountText');
            donCountText.text = `x ${this.cardInstance.attachedDon.length}`;

            donCountContainer.visible = true;
        } else {
            this.getChildByLabel('donCountContainer').visible = false;
        }

        const summoningSickness = cardVisualsContainer.getChildByLabel('summoningSickness');
        if (this?.cardInstance?.isPlayedThisTurn && !this?.cardInstance?.hasRush && this?.cardInstance?.location === 'field_characters') {
            summoningSickness.visible = true;
        } else {
            summoningSickness.visible = false;
        }

        const blockerFrame = cardVisualsContainer.getChildByLabel('blockerFrame');
        const blockerIcon = cardVisualsContainer.getChildByLabel('blockerIcon');
        if (this?.cardInstance?.isBlocker && this?.cardInstance?.location === 'field_characters') {
            blockerFrame.visible = true;
            // blockerIcon.visible = true;
        } else {
            blockerFrame.visible = false;
            // blockerIcon.visible = false;
        }

        if (this?.cardInstance?.currentCost || this?.cardInstance?.currentCost === 0) {
            this.rebuildCostContainer(this?.cardInstance?.color?.[0], this?.cardInstance?.currentCost);
        } else {
            this.hideCost();
        }
    }

    #calculatePowerTextColor(basePower, currentPower) {
        if (currentPower > basePower) {
            return '#fbe64e';
        } else if (currentPower === basePower) {
            return '#ffffff';
        } else {
            return '#ff0000';
        }
    }

    showFrontFace() {
        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');
        cardVisualsContainer.getChildByLabel('frontFaceSprite').visible = true;
        cardVisualsContainer.getChildByLabel('backFaceSprite').visible = false;
    }

    showBackFace() {
        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');
        cardVisualsContainer.getChildByLabel('frontFaceSprite').visible = false;
        cardVisualsContainer.getChildByLabel('backFaceSprite').visible = true;
    }

    setHighlightPlayable() {
        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');
        if (this?.cardInstance?.isBlocker && this?.cardInstance?.location === 'field_characters') {
            const blockerFrameHighlightMask = cardVisualsContainer.getChildByLabel('blockerFrameHighlightMask');
            blockerFrameHighlightMask.visible = true;
            blockerFrameHighlightMask.scale.set(1.16);
        } else {
            const cardHighlight = cardVisualsContainer.getChildByLabel('cardHighlight');
            cardHighlight.visible = true;
            cardHighlight.scale.set(0.64, 0.64*(65/62));
        }
        cardVisualsContainer.setHighlightParameters(4.09, 4.59, '#0063e6');
    }

    setHighlightSelectable() {
        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');
        if (this?.cardInstance?.isBlocker && this?.cardInstance?.location === 'field_characters') {
            const blockerFrameHighlightMask = cardVisualsContainer.getChildByLabel('blockerFrameHighlightMask');
            blockerFrameHighlightMask.visible = true;
            blockerFrameHighlightMask.scale.set(1.16);
        } else {
            const cardHighlight = cardVisualsContainer.getChildByLabel('cardHighlight');
            cardHighlight.visible = true;
            cardHighlight.scale.set(0.66, 0.66*(65/62));
        }
        cardVisualsContainer.setHighlightParameters(4.09, 4.59, '#0063e6');
    }

    setHighlightSelected() {
        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');
        if (this?.cardInstance?.isBlocker && this?.cardInstance?.location === 'field_characters') {
            const blockerFrameHighlightMask = cardVisualsContainer.getChildByLabel('blockerFrameHighlightMask');
            blockerFrameHighlightMask.visible = true;
            blockerFrameHighlightMask.scale.set(1.25);
        } else {
            const cardHighlight = cardVisualsContainer.getChildByLabel('cardHighlight');
            cardHighlight.visible = true;
            cardHighlight.scale.set(0.69, 0.69*(65/62));
        }
        cardVisualsContainer.setHighlightParameters(2.5, 5, '#0088f0');
    }

    setHighlightAttackTarget() {
        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');
        if (this?.cardInstance?.isBlocker && this?.cardInstance?.location === 'field_characters') {
            const blockerFrameHighlightMask = cardVisualsContainer.getChildByLabel('blockerFrameHighlightMask');
            blockerFrameHighlightMask.visible = true;
            blockerFrameHighlightMask.scale.set(1.16);
        } else {
            const cardHighlight = cardVisualsContainer.getChildByLabel('cardHighlight');
            cardHighlight.visible = true;
            cardHighlight.scale.set(0.64, 0.64*(65/62));
        }
        cardVisualsContainer.setHighlightParameters(4.09, 4.59, '#ac1111');
    }

    highlightCard() {
        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');
        if (this?.cardInstance?.isBlocker && this?.cardInstance?.location === 'field_characters') {
            cardVisualsContainer.getChildByLabel('blockerFrameHighlightMask').visible = true;
        } else {
            cardVisualsContainer.getChildByLabel('cardHighlight').visible = true;
        }
    }

    unhighlightCard() {
        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');
        cardVisualsContainer.getChildByLabel('blockerFrameHighlightMask').visible = false;
        cardVisualsContainer.getChildByLabel('cardHighlight').visible = false;
    }

    hidePowerText() {
        this.getChildByLabel('powerTextContainer').visible = false;
    }

    showPowerText() {
        this.getChildByLabel('powerTextContainer').visible = true;
    }

    #updateRestedVisuals() {
        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');
        if (!this?.cardInstance?.isRested || !this.#showRested) {
            cardVisualsContainer.getChildByLabel('restedOverlay').visible = false;
            cardVisualsContainer.getChildByLabel('restedZzz').visible = false;
            cardVisualsContainer.angle = 0;
        } else {
            cardVisualsContainer.getChildByLabel('restedOverlay').visible = true;
            cardVisualsContainer.getChildByLabel('restedZzz').visible = true;
            cardVisualsContainer.angle = 7;
        }
    }

    get showRested() {
        return this.#showRested;
    }

    set showRested(showRested) {
        this.#showRested = showRested;
        this.#updateRestedVisuals();
    }

    async playPlayerAttachedDonAnimation() {
        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');
        const circle01 = cardVisualsContainer.getChildByLabel('circle01');
        circle01.visible = true;
        circle01.alpha = 0;
        circle01.scale.set(0.5);

        const alphaTimeline = gsap.timeline();
        alphaTimeline.to(circle01, {
            duration: 0.1,
            pixi: {
                alpha: 1,
            }
        }).to(circle01, {
            duration: 2,
            ease: 'power2.out',
            pixi: {
                alpha: 0,
            }
        });

        const scaleTimeline = gsap.timeline();
        scaleTimeline.to(circle01, {
            duration: 2.1,
            ease: 'power2.out',
            pixi: {
                scale: 3,
            }
        });

        await Promise.all([alphaTimeline, scaleTimeline]);

        circle01.visible = false;
    }

    showSkull() {
        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');
        const skull = cardVisualsContainer.getChildByLabel('skull');
        skull.visible = true;
    }

    hideSkull() {
        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');
        const skull = cardVisualsContainer.getChildByLabel('skull');
        skull.visible = false;
    }

    hideSummoningSickness() {
        const cardVisualsContainer = this.getChildByLabel('cardVisualsContainer');
        const summoningSickness = cardVisualsContainer.getChildByLabel('summoningSickness');
        summoningSickness.visible = false;
    }

    setDonsForReturnCounter(donsCount) {
        if (this?.cardInstance?.attachedDon?.length) {
            const donCountContainer = this.getChildByLabel('donCountContainer');
            const donCountText = donCountContainer.getChildByLabel('donCountText');
            donCountText.text = `x ${this.cardInstance.attachedDon.length}-${donsCount}`;

            donCountContainer.visible = true;
        } else {
            this.getChildByLabel('donCountContainer').visible = false;
        }
    }

    setAttachedDonsCounter(donsCount) {
        if (donsCount > 0) {
            const donCountContainer = this.getChildByLabel('donCountContainer');
            const donCountText = donCountContainer.getChildByLabel('donCountText');
            donCountText.text = `x ${donsCount}`;

            donCountContainer.visible = true;
        } else {
            this.getChildByLabel('donCountContainer').visible = false;
        }
    }

    async playPowerChangedAnimation(prevPower, currentPower) {
        const powerText = this.getChildByLabel('powerText', true);
        powerText.text = currentPower.toString();

        const dummy = { power: prevPower, color: powerText.style.fill };
        await gsap.timeline({
            onUpdate: () => {
                powerText.text = Math.floor(dummy.power).toString();
                powerText.style.fill = dummy.color;
            }
        }).to(dummy, {
            duration: 0.5,
            power: currentPower,
            color: this.#calculatePowerTextColor(this.cardInstance.power, currentPower),
        });
    }

    disableEvents() {
        this.isEventsDisabled = true;
    }

    enableEvents() {
        this.isEventsDisabled = false;
    }

    disableTapping() {
        this.isTappedDisabled = true;
    }

    enableTapping() {
        this.isTappedDisabled = false;
    }

    enableEventPropagation() {
        this.shouldStopEventPropagation = false;
    }

    disableEventPropagation() {
        this.shouldStopEventPropagation = true;
    }

    hideCost() {
        this.costContainer.visible = false;
    }

    showCost() {
        this.costContainer.visible = true;
    }
}
